import {
    ChangeDetectionStrategy,
    Component,
    DestroyRef,
    Input,
    OnInit,
    Optional,
    Self,
    inject,
    signal,
    computed
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { DecimalPipe } from "@angular/common";
import { ControlValueAccessor, NgControl } from "@angular/forms";
import { MatCalendar } from "@angular/material/datepicker";
import { CdkConnectedOverlay, CdkOverlayOrigin, ConnectedPosition } from "@angular/cdk/overlay";
import { TZDate, tzName } from "@date-fns/tz";
import { CalendarHeaderComponent } from "./calendar-header.component";

@Component({
    selector: "app-date-picker",
    templateUrl: "./date-picker.component.html",
    styleUrl: "./date-picker.component.scss",
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [MatCalendar, CdkConnectedOverlay, CdkOverlayOrigin, DecimalPipe]
})
export class DatePickerComponent implements ControlValueAccessor, OnInit {
    readonly calendarHeader = CalendarHeaderComponent;

    /**
     * IANA timezone string used for display and value conversion.
     * The selected UTC value is converted to this timezone for display, and
     * user selections are converted back to UTC before emitting.
     * Must be a valid IANA timezone identifier (e.g. `"America/New_York"`, `"Europe/London"`).
     * @default "America/New_York"
     */
    @Input() timezone: string = "America/New_York";

    private readonly destroyRef = inject(DestroyRef);

    constructor(@Optional() @Self() public ngControl: NgControl | null) {
        if (this.ngControl) {
            this.ngControl.valueAccessor = this;
        }
    }

    isOpen = signal(false);
    isDisabled = signal(false);
    isInvalid = signal(false);

    selectedDate = signal<Date | null>(null);
    selectedHour = signal<number | null>(null);
    selectedMinute = signal<number | null>(null);
    isPM = signal<boolean | null>(null);

    readonly overlayPositions: ConnectedPosition[] = [
        { originX: "start", originY: "bottom", overlayX: "start", overlayY: "top", offsetY: 4 },
        { originX: "start", originY: "top", overlayX: "start", overlayY: "bottom", offsetY: -4 }
    ];

    readonly hours: number[] = [12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];
    readonly minutes: number[] = Array.from({ length: 60 }, (_, i) => i);

    ngOnInit(): void {
        if (!Intl.supportedValuesOf("timeZone").includes(this.timezone)) {
            throw new Error(`DatePickerComponent: invalid timezone "${this.timezone}"`);
        }

        const control = this.ngControl?.control;
        if (control) {
            control.statusChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.updateInvalid());
            this.updateInvalid();
        }
    }

    private updateInvalid(): void {
        this.isInvalid.set(!!(this.ngControl?.invalid && this.ngControl?.touched));
    }

    tzAbbr = computed(() => tzName(this.timezone, this.selectedDate() ?? new Date(), "short"));

    displayValue = computed(() => {
        const date = this.selectedDate();
        if (!date) return "Choose date and time";
        return this.formatDisplay(date, this.selectedHour() ?? 12, this.selectedMinute() ?? 0, this.isPM() ?? false);
    });

    private onChange: (value: Date | null) => void = () => {};
    private onTouched: () => void = () => {};

    writeValue(value: Date | null): void {
        if (value) {
            const zoned = new TZDate(value, this.timezone);
            const hours24 = zoned.getHours();
            this.selectedDate.set(new Date(zoned.getFullYear(), zoned.getMonth(), zoned.getDate()));
            this.isPM.set(hours24 >= 12);
            this.selectedHour.set(hours24 % 12 || 12);
            this.selectedMinute.set(zoned.getMinutes());
        } else {
            this.selectedDate.set(null);
            this.selectedHour.set(null);
            this.selectedMinute.set(null);
            this.isPM.set(null);
        }
    }

    registerOnChange(fn: (value: Date | null) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.isDisabled.set(isDisabled);
    }

    toggle(): void {
        if (this.isDisabled()) return;
        this.isOpen.set(true);
    }

    close(): void {
        this.isOpen.set(false);
        this.onTouched();
        this.updateInvalid();
    }

    onDateSelected(date: Date | null): void {
        this.selectedDate.set(date);
        if (date !== null) {
            if (this.selectedHour() === null) this.selectedHour.set(12);
            if (this.selectedMinute() === null) this.selectedMinute.set(0);
            if (this.isPM() === null) this.isPM.set(false);
        }
        this.emitValue();
    }

    selectHour(event: Event): void {
        if (event instanceof KeyboardEvent && event.key !== "Enter" && event.key !== " ") return;
        const value = (event.target as HTMLElement).closest("button")?.dataset["value"];
        if (value === undefined) return;
        this.selectedHour.set(+value);
        this.emitValue();
    }

    selectMinute(event: Event): void {
        if (event instanceof KeyboardEvent && event.key !== "Enter" && event.key !== " ") return;
        const value = (event.target as HTMLElement).closest("button")?.dataset["value"];
        if (value === undefined) return;
        this.selectedMinute.set(+value);
        this.emitValue();
    }

    setAM(): void {
        this.isPM.set(false);
        this.emitValue();
    }

    setPM(): void {
        this.isPM.set(true);
        this.emitValue();
    }

    private emitValue(): void {
        const date = this.selectedDate();
        if (!date) return;
        let hours = this.selectedHour() ?? 12;
        const pm = this.isPM() ?? false;
        if (pm && hours !== 12) hours += 12;
        if (!pm && hours === 12) hours = 0;
        const utc = new TZDate(
            date.getFullYear(),
            date.getMonth(),
            date.getDate(),
            hours,
            this.selectedMinute() ?? 0,
            this.timezone
        );
        this.onChange(new Date(utc));
    }

    private formatDisplay(date: Date, hour: number, minute: number, isPM: boolean): string {
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        const year = date.getFullYear();
        const h = String(hour).padStart(2, "0");
        const m = String(minute).padStart(2, "0");
        const period = isPM ? "PM" : "AM";
        return `${month}/${day}/${year} ${h}:${m} ${period} ${this.tzAbbr()}`;
    }
}
